import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Upload, FileText } from 'lucide-react'
import toast from 'react-hot-toast'
import cd2bLiquidacionService from '@/services/cd2bLiquidacionService'
import PageContainer from '@/components/common/PageContainer'
import DataTable from '@/components/common/DataTable'
import { EmptyState } from '@/components/common/EmptyState'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

/**
 * Documentos fiscales de tus pagos como asesor cd-2b. COREDESK SV te paga contra un documento:
 *  - si NO tienes NRC, la empresa te emite una Factura de Sujeto Excluido (necesita tu DUI o NIT
 *    y tu dirección con los códigos del MH);
 *  - si tienes NRC, subes aquí el CCF que le emitiste por tu comisión.
 * Tu pago queda completado cuando el documento está listo.
 */

const ESTADO_DOC = {
  pendiente: { label: 'Preparando documento', variant: 'warning' },
  esperando_ccf: { label: 'Falta tu CCF', variant: 'danger' },
  listo: { label: 'Documento listo', variant: 'success' },
  error: { label: 'Con error', variant: 'danger' },
}

const vacio = {
  tipoDocumento: '13', numDocumento: '', codActividad: '', descActividad: '', telefono: '', correo: '',
  direccion: { departamento: '', municipio: '', distrito: '', complemento: '' },
}

const usd = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`
const errorDe = (e, porDefecto) => e?.response?.data?.error || porDefecto

export default function DocumentosFiscales() {
  const navigate = useNavigate()
  const [fiscal, setFiscal] = useState(vacio)
  const [registro, setRegistro] = useState({ taxId: null, nrc: null })
  const [codigo2fa, setCodigo2fa] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [pagos, setPagos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [subiendo, setSubiendo] = useState(null)

  const cargar = useCallback(async () => {
    setCargando(true)
    try {
      const [df, pg] = await Promise.all([cd2bLiquidacionService.datosFiscales(), cd2bLiquidacionService.pagos()])
      setRegistro({ taxId: df.data?.taxId, nrc: df.data?.nrc })
      if (df.data?.fiscal) setFiscal({ ...vacio, ...df.data.fiscal, direccion: { ...vacio.direccion, ...(df.data.fiscal.direccion || {}) } })
      setPagos(pg.data || [])
    } catch (e) {
      toast.error(errorDe(e, 'No se pudieron cargar tus documentos'))
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => { cargar() }, [cargar])

  const campo = (k) => (e) => setFiscal((f) => ({ ...f, [k]: e.target.value }))
  const campoDir = (k) => (e) => setFiscal((f) => ({ ...f, direccion: { ...f.direccion, [k]: e.target.value } }))

  const guardar = async (e) => {
    e.preventDefault()
    setGuardando(true)
    try {
      await cd2bLiquidacionService.guardarDatosFiscales(fiscal, codigo2fa)
      toast.success('Datos fiscales guardados')
      setCodigo2fa('')
      await cargar()
    } catch (err) {
      toast.error(errorDe(err, 'No se pudieron guardar'))
    } finally {
      setGuardando(false)
    }
  }

  const subirCcf = async (payoutId, archivo) => {
    if (!archivo) return
    setSubiendo(payoutId)
    try {
      const texto = await archivo.text()
      await cd2bLiquidacionService.subirCcf(payoutId, texto)
      toast.success('CCF recibido: lo estamos registrando')
      await cargar()
    } catch (err) {
      toast.error(errorDe(err, 'No se pudo subir el CCF'))
    } finally {
      setSubiendo(null)
    }
  }

  const conNrc = Boolean(registro.nrc)

  const columnasPagos = [
    {
      clave: 'pago', titulo: 'Pago', prioridad: 'titulo',
      celda: (p) => {
        const l = p.liquidacion
        const ref = l?.fse?.numeroControl || l?.ccf?.numeroControl || p.cd2b?.documentoRef?.numeroControl
        return (
          <div className="min-w-0">
            <p className="font-semibold text-foreground">{usd(p.amounts?.netAmount)} · {p.cd2b?.documento === 'FSE' ? 'Factura de Sujeto Excluido' : 'Tu CCF'}</p>
            {ref && <p className="break-all font-mono text-caption text-subtle">{ref}</p>}
            {l?.ultimoError && <p className="text-caption text-danger-700 dark:text-danger-300">{l.ultimoError}</p>}
          </div>
        )
      },
    },
    {
      clave: 'estado', titulo: 'Estado', prioridad: 'valor',
      celda: (p) => {
        const est = ESTADO_DOC[p.cd2b?.documentoEstado] || null
        return est ? <Badge variant={est.variant}>{est.label}</Badge> : <span className="text-subtle">—</span>
      },
    },
    {
      clave: 'accion', titulo: '', alineacion: 'derecha', prioridad: 'acciones',
      celda: (p) => (p.cd2b?.documentoEstado === 'esperando_ccf' ? (
        <label className="inline-flex h-10 w-full cursor-pointer items-center justify-center gap-1.5 rounded-md border border-line bg-card px-3 text-body-sm font-medium text-primary hover:bg-surface-muted md:w-auto">
          <Upload size={16} />
          {subiendo === p._id ? 'Subiendo…' : 'Subir CCF (JSON)'}
          <input
            type="file"
            accept="application/json,.json"
            className="hidden"
            disabled={subiendo === p._id}
            onChange={(e) => subirCcf(p._id, e.target.files?.[0])}
          />
        </label>
      ) : null),
    },
  ]

  return (
    <PageContainer>
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/commissions')} aria-label="Volver">
          <ArrowLeft size={18} />
        </Button>
        <div className="min-w-0">
          <h2 className="font-display text-h1 font-semibold leading-tight text-foreground">
            Documentos fiscales
          </h2>
          <p className="mt-0.5 text-body-sm text-subtle">
            {conNrc
              ? 'Tienes NRC: por cada pago sube el CCF que le emites a COREDESK SV por tus pagos.'
              : 'Sin NRC: COREDESK SV te emite una Factura de Sujeto Excluido en cada pago y retiene la renta.'}
          </p>
        </div>
      </div>

      <Card className="p-4">
        <form onSubmit={guardar} className="space-y-3">
          <h3 className="font-semibold text-foreground">Tus datos fiscales</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            <div>
              <Label htmlFor="tipoDocumento">Documento</Label>
              <Select value={fiscal.tipoDocumento} onValueChange={(v) => setFiscal((f) => ({ ...f, tipoDocumento: v }))}>
                <SelectTrigger id="tipoDocumento"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="13">DUI</SelectItem>
                  <SelectItem value="36">NIT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="numDocumento">Número</Label>
              <Input id="numDocumento" value={fiscal.numDocumento} onChange={campo('numDocumento')} placeholder={fiscal.tipoDocumento === '13' ? '00000000-0' : '0000-000000-000-0'} />
            </div>
            <div>
              <Label htmlFor="codActividad">Actividad económica (código MH, opcional)</Label>
              <Input id="codActividad" value={fiscal.codActividad || ''} onChange={campo('codActividad')} placeholder="74909" />
            </div>
            <div>
              <Label htmlFor="departamento">Departamento (código)</Label>
              <Input id="departamento" value={fiscal.direccion.departamento} onChange={campoDir('departamento')} placeholder="06" maxLength={2} />
            </div>
            <div>
              <Label htmlFor="municipio">Municipio (código)</Label>
              <Input id="municipio" value={fiscal.direccion.municipio} onChange={campoDir('municipio')} placeholder="23" maxLength={2} />
            </div>
            <div>
              <Label htmlFor="distrito">Distrito (código)</Label>
              <Input id="distrito" value={fiscal.direccion.distrito} onChange={campoDir('distrito')} placeholder="14" maxLength={2} />
            </div>
            <div className="sm:col-span-2 md:col-span-3">
              <Label htmlFor="complemento">Dirección</Label>
              <Input id="complemento" value={fiscal.direccion.complemento} onChange={campoDir('complemento')} placeholder="Calle, colonia, número" />
            </div>
            <div>
              <Label htmlFor="telefono">Teléfono</Label>
              <Input id="telefono" value={fiscal.telefono || ''} onChange={campo('telefono')} />
            </div>
            <div>
              <Label htmlFor="correo">Correo</Label>
              <Input id="correo" type="email" value={fiscal.correo || ''} onChange={campo('correo')} />
            </div>
            <div>
              <Label htmlFor="codigo2fa">Código de tu app autenticadora</Label>
              <Input id="codigo2fa" inputMode="numeric" value={codigo2fa} onChange={(e) => setCodigo2fa(e.target.value)} placeholder="123456" />
            </div>
          </div>
          <Button type="submit" className="w-full sm:w-auto" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar datos fiscales'}</Button>
        </form>
      </Card>

      <section className="space-y-3" aria-labelledby="titulo-doc-pagos">
        <h3 id="titulo-doc-pagos" className="font-semibold text-foreground">Documento de cada pago</h3>
        <DataTable
          etiquetaTabla="Documento de cada pago"
          columnas={columnasPagos}
          filas={pagos}
          claveDeFila={(p) => p._id}
          cargando={cargando}
          tarjetasEnMovil
          className="bg-card"
          vacio={<EmptyState icono={<FileText size={24} />} titulo="Todavía no tienes pagos con documento" descripcion="Cuando te paguemos una comisión, aquí verás su factura o el CCF que te toca subir." />}
        />
      </section>
    </PageContainer>
  )
}
